import { useTheme } from '@material-ui/core';
import { CustomLayer, ResponsiveLine } from '@nivo/line';
import { area, format } from 'd3';
import * as React from 'react';
import AnalysisChartLoading from '../../AnalysisChartLoading';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { formatNumber } from '../../../../logic/otherUtilities/formatting/formatNumber';
import { ChartProps } from '../../AnalysisList';
import { internationalText } from '../../internationalText';
import DataTimedOut, { didQueryTimeout } from '../DataTimedOut';
import NotEnoughData from '../NotEnoughData';

export interface MarketTrafficCostProps extends ChartProps {}

const MarketTrafficCost: React.FC<MarketTrafficCostProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
}) => {
  // Adjust shown filters
  React.useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showTargetRankValues: true,
          showTagGroupId: true,
          showTagId: true,
        },
      })),
    []
  );

  // Data fetching
  const dataQuery = projectsApiService.queries.analysis.useGetMarketPotential({
    projectId: project.id,
    serpOnly: analysisFilterState?.serpOnly,
    rankValues: analysisFilterState?.targetRankValues,
    tagGroupId: analysisFilterState?.tagGroupId,
    tagId: analysisFilterState?.tagId,
  });

  const dataLoading = dataQuery?.query?.loading;
  const dataResult = dataQuery?.result ?? null;

  // Data tranformation
  const lowCost = dataQuery?.result?.projectTrafficDeltaLowCost ?? 0;
  const highCost = dataQuery?.result?.projectTrafficDeltaHighCost ?? 0;
  const avgCost = (lowCost + highCost) / 2;
  const currencyConversionRate = !!project?.currencyConversionRate
    ? project?.currencyConversionRate
    : 1;

  const data = [
    {
      id: 'lowCost',
      data: [0, 1, 2, 3].map((year) => ({
        x: year,
        y: ((year * 12 * lowCost) / 1_000_000) * currencyConversionRate,
      })),
    },
    {
      id: 'avgCost',
      data: [0, 1, 2, 3].map((year) => ({
        x: year,
        y: ((year * 12 * avgCost) / 1_000_000) * currencyConversionRate,
      })),
    },
    {
      id: 'highCost',
      data: [0, 1, 2, 3].map((year) => ({
        x: year,
        y: ((year * 12 * highCost) / 1_000_000) * currencyConversionRate,
      })),
    },
  ];

  // Styling
  const theme = useTheme();
  const chartTheme = {
    theme: {
      fontSize: 14,
      color: theme.palette.info.main,
      labels: {
        text: {
          fontSize: 14,
          color: theme.palette.info.main,
        },
      },
    },
  };

  const d3Format = format('.2s');

  if (didQueryTimeout(dataQuery?.query)) {
    return <DataTimedOut />;
  }

  if (dataLoading && !dataResult) {
    return <AnalysisChartLoading />;
  }

  if (!(dataResult?.projectSearchVolume > 0)) {
    return <NotEnoughData />;
  }

  return (
    <div data-screenshot-id="chart-loaded">
      <div className="h-[400px]">
        <ResponsiveLine
          theme={chartTheme.theme}
          data={data}
          margin={{ top: 50, right: 50, bottom: 50, left: 70 }}
          axisLeft={{
            format: '~s',
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 0,
            legend: project.currency ?? 'SEK',
            legendPosition: 'middle',
            legendOffset: -60,
          }}
          axisBottom={{
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 0,
            format: (v: number) => {
              return internationalText.chart.traffic_cost.year[language] + ' ' + v;
            },
            legendOffset: -12,
          }}
          layers={[
            'grid',
            'markers',
            AreaLayer,
            LabelLayer,
            'axes',
            'areas',
            'crosshair',
            'lines',
            'points',
            'slices',
            'mesh',
            'legends',
          ]}
          colors={['rgba(92, 106, 123, 0.3)', 'rgba(0,0,0,0)', 'rgba(92, 106, 123, 0.3)']}
          pointColor={(p: any) => {
            return 'rgba(92, 106, 123, 0)';
          }}
          pointSize={8}
          animate={false}
          curve={'monotoneX'}
          isInteractive={false}
        />
      </div>
      <div className="flex gap-2 flex-col justify-center items-center pt-2 font-pixel-charts">
        <div className="text-sm text-voxel-blue-700">
          {internationalText.chart.traffic_cost.monthly_increase[language]}:{' '}
          <span className="font-bold pl-1 text-base">
            {formatNumber(dataResult.projectTrafficPotential - dataResult.projectTrafficToday)}{' '}
            {internationalText.chart.traffic_cost.visits[language]}
          </span>
        </div>
        <div className="text-sm text-voxel-blue-700">
          {internationalText.chart.traffic_cost.yearly_cost[language]}:
          <span className="font-bold pl-1 text-base">
            {d3Format((lowCost / 1000000) * 12)} - {d3Format((highCost / 1000000) * 12)}
            {project.currency}
          </span>
        </div>
        <div className="text-sm text-voxel-blue-700">
          {internationalText.chart.traffic_cost.explanation[language]}
        </div>
      </div>
    </div>
  );
};

interface InputData {
  x: number;
  bottomLine: number;
  topLine: number;
}
const AreaLayer: CustomLayer = (props) => {
  const xScale: any = props.xScale;
  const yScale: any = props.yScale;
  const inputData = props.data[0].data.map(
    (_, index): InputData => ({
      x: props.data[0].data[index].x as number,
      bottomLine: props.data[0].data[index].y as number,
      topLine: props.data[2].data[index].y as number,
    })
  );

  const areaGenerator = area<InputData>()
    .x((d) => {
      return xScale(d.x);
    })
    .y0((d) => yScale(d.bottomLine))
    .y1((d) => yScale(d.topLine));

  return (
    <>
      <path d={areaGenerator(inputData)} fill="rgba(92, 106, 123, 0.15)" fillOpacity={10} />
    </>
  );
};

const LabelLayer: CustomLayer = (props) => {
  const xScale: any = props.xScale;
  const yScale: any = props.yScale;
  const d3Format = format('.2s');

  return (
    <>
      {props.data[2].data.slice(1).map((point) => (
        <text
          x={xScale(point.x)}
          y={yScale(point.y) - 12}
          textAnchor="middle"
          className="fill-voxel-gray-700 font-bold"
        >
          {d3Format(point.y as any)}
        </text>
      ))}
    </>
  );
};

export default MarketTrafficCost;
